import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import { alpha } from '@mui/material/styles';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { type ChangeEvent, type ReactNode, useLayoutEffect, useRef } from 'react';
import {
  GLASS_BACKGROUND,
  GLASS_BLUR,
  NAVY,
  RADIUS_CARD,
  RADIUS_ITEM,
  SHADOW,
  SURFACE_CONTAINER_LOWEST,
  YELLOW,
} from 'styles/tokens';

type FormFieldOwnProps = {
  hint?: string;
  error?: string | null;
  required?: boolean;
  /** When true, renders the discreet "(opcional)" marker next to the label. See design-system §21.3. */
  optional?: boolean;
  /** When true, shows a green check icon inside the input as inline confirmation. See design-system §21.4. */
  success?: boolean;
  /**
   * When true, displays integer values with es-CL thousand separators while typing (1234567 → 1.234.567).
   * The input is rendered as type="text" + inputMode="numeric", and onChange receives the raw digits string.
   */
  thousandSeparator?: boolean;
};

const thousandsFormatter = new Intl.NumberFormat('es-CL');

const formatThousands = (raw: unknown): string => {
  const digits = String(raw ?? '').replace(/\D/g, '');
  if (digits === '') return '';
  const n = Number(digits);
  return Number.isFinite(n) ? thousandsFormatter.format(n) : '';
};

export type FormFieldProps = Omit<TextFieldProps, 'error' | 'helperText' | 'variant' | 'label'> &
  FormFieldOwnProps & {
    label?: ReactNode;
  };

const FormField = ({
  hint,
  error,
  required,
  optional,
  success,
  thousandSeparator,
  label,
  sx,
  slotProps,
  id,
  type,
  inputMode,
  value,
  onChange,
  ...rest
}: FormFieldProps) => {
  const helperText = error ?? hint ?? undefined;
  const fieldId = id ?? (typeof label === 'string' ? `field-${label.toLowerCase().replace(/\s+/g, '-')}` : undefined);

  const displayValue = thousandSeparator ? formatThousands(value) : value;
  const effectiveType = thousandSeparator ? 'text' : type;
  const effectiveInputMode = thousandSeparator ? 'numeric' : inputMode;

  const inputRef = useRef<HTMLInputElement | null>(null);
  const pendingCaretDigitCountRef = useRef<number | null>(null);

  const handleChange: TextFieldProps['onChange'] = thousandSeparator
    ? (e) => {
        const rawValue = e.target.value;
        const selectionStart = e.target.selectionStart ?? rawValue.length;
        pendingCaretDigitCountRef.current = rawValue.slice(0, selectionStart).replace(/\D/g, '').length;
        const digits = rawValue.replace(/\D/g, '');
        const synthetic = {
          ...e,
          target: { ...e.target, value: digits, name: e.target.name } as HTMLInputElement,
          currentTarget: e.currentTarget,
        } as ChangeEvent<HTMLInputElement>;
        onChange?.(synthetic);
      }
    : onChange;

  useLayoutEffect(() => {
    if (!thousandSeparator) return;
    const inputEl = inputRef.current;
    const target = pendingCaretDigitCountRef.current;
    if (!inputEl || target == null) return;
    const formatted = String(displayValue ?? '');
    let count = 0;
    let pos = 0;
    while (pos < formatted.length && count < target) {
      if (/\d/.test(formatted[pos])) count += 1;
      pos += 1;
    }
    inputEl.setSelectionRange(pos, pos);
    pendingCaretDigitCountRef.current = null;
  }, [displayValue, thousandSeparator]);

  const idleRing = `inset 0 0 0 1px ${alpha(NAVY, 0.08)}`;
  const hoverRing = `inset 0 0 0 1px ${alpha(NAVY, 0.18)}`;
  const focusRing = `inset 0 0 0 1.5px ${NAVY}, 0 0 0 4px ${alpha(YELLOW, 0.25)}`;
  const errorRing = `inset 0 0 0 1.5px #E7233B, 0 0 0 4px ${alpha('#E7233B', 0.18)}`;

  const userInputSlot = (typeof slotProps?.input === 'object' && slotProps?.input) || {};
  const userSelectSlot = (typeof slotProps?.select === 'object' && slotProps?.select) || {};
  const successAdornment =
    success && !error ? (
      <InputAdornment position="end">
        <CheckCircleOutlinedIcon sx={{ fontSize: 18, color: '#53B721' }} aria-label="Campo válido" />
      </InputAdornment>
    ) : undefined;

  const userMenuProps = (userSelectSlot as { MenuProps?: { slotProps?: { paper?: { sx?: object } } } }).MenuProps ?? {};
  const userPaperSlotSx = userMenuProps.slotProps?.paper?.sx ?? {};
  const glassMenuPaperSx = {
    backdropFilter: GLASS_BLUR,
    backgroundColor: GLASS_BACKGROUND,
    boxShadow: SHADOW,
    borderRadius: RADIUS_CARD,
    border: `1px solid ${alpha(NAVY, 0.06)}`,
    mt: 0.5,
    p: 0.5,
    '& .MuiList-root': { p: 0 },
    '& .MuiMenuItem-root': {
      fontFamily: 'Jost, sans-serif',
      fontSize: '0.9375rem',
      color: NAVY,
      borderRadius: RADIUS_ITEM,
      mx: 0,
      my: 0.25,
      px: 1.25,
      py: 0.875,
      minHeight: 'auto',
      transition: 'background-color 0.15s ease',
      '&:hover': { backgroundColor: alpha(NAVY, 0.06) },
      '&.Mui-selected': {
        backgroundColor: alpha(YELLOW, 0.18),
        '&:hover': { backgroundColor: alpha(YELLOW, 0.25) },
      },
      '&.Mui-disabled': { color: alpha(NAVY, 0.35) },
    },
    ...userPaperSlotSx,
  };
  const mergedMenuProps = {
    ...userMenuProps,
    slotProps: {
      ...(userMenuProps.slotProps ?? {}),
      paper: {
        ...(userMenuProps.slotProps?.paper ?? {}),
        sx: glassMenuPaperSx,
      },
    },
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: rest.fullWidth === false ? 'auto' : '100%',
      }}
    >
      {label && (
        <Typography
          component="label"
          htmlFor={fieldId}
          sx={{
            fontFamily: 'Jost, sans-serif',
            fontSize: '0.8125rem',
            fontWeight: 500,
            color: alpha(NAVY, 0.7),
            mb: 0.875,
            letterSpacing: '0.005em',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.25,
            transition: 'color 0.18s ease',
          }}
        >
          {label}
          {required && (
            <Typography
              component="span"
              sx={{ color: '#E7233B', fontFamily: 'Jost, sans-serif', fontWeight: 600, ml: 0.25 }}
              aria-hidden
            >
              *
            </Typography>
          )}
          {optional && !required && (
            <Typography
              component="span"
              sx={{
                color: alpha(NAVY, 0.45),
                fontFamily: 'Jost, sans-serif',
                fontWeight: 400,
                fontSize: '0.75rem',
                ml: 0.5,
              }}
            >
              (opcional)
            </Typography>
          )}
        </Typography>
      )}
      <TextField
        {...rest}
        id={fieldId}
        type={effectiveType}
        inputMode={effectiveInputMode}
        inputRef={thousandSeparator ? inputRef : rest.inputRef}
        value={displayValue}
        onChange={handleChange}
        error={!!error}
        helperText={helperText}
        size={rest.size ?? 'small'}
        fullWidth={rest.fullWidth ?? true}
        slotProps={{
          ...slotProps,
          inputLabel: { shrink: true, sx: { display: 'none' } },
          input: {
            ...userInputSlot,
            notched: false,
            ...(successAdornment ? { endAdornment: successAdornment } : {}),
          },
          select: {
            ...userSelectSlot,
            MenuProps: mergedMenuProps,
          },
        }}
        sx={{
          fontFamily: 'Jost, sans-serif',
          '& .MuiInputBase-root, & .MuiFormHelperText-root': {
            fontFamily: 'Jost, sans-serif',
          },
          '& .MuiOutlinedInput-root': {
            borderRadius: RADIUS_ITEM,
            bgcolor: SURFACE_CONTAINER_LOWEST,
            boxShadow: idleRing,
            transition: 'box-shadow 0.18s ease, background-color 0.18s ease',
            '& fieldset': { border: 'none' },
            '&:hover': { boxShadow: hoverRing, bgcolor: SURFACE_CONTAINER_LOWEST },
            '&.Mui-focused': { boxShadow: focusRing },
            '&.Mui-error': { boxShadow: errorRing },
            '&.Mui-disabled': {
              boxShadow: `inset 0 0 0 1px ${alpha(NAVY, 0.06)}`,
              bgcolor: alpha(NAVY, 0.02),
            },
          },
          '& .MuiOutlinedInput-input': {
            color: NAVY,
            py: 1.4,
            px: 1.75,
            fontSize: '0.9375rem',
            letterSpacing: '0.005em',
            '&::placeholder': { color: alpha(NAVY, 0.35), opacity: 1 },
            '&[type=number]': { MozAppearance: 'textfield' },
            '&[type=number]::-webkit-outer-spin-button, &[type=number]::-webkit-inner-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
            },
          },
          '& .MuiInputAdornment-root': { mr: 1 },
          '& .MuiInputBase-multiline': {
            py: 1.4,
            px: 1.75,
            '& .MuiOutlinedInput-input': { p: 0 },
          },
          '& .MuiFormHelperText-root': {
            mx: 0,
            mt: 0.625,
            fontSize: '0.75rem',
            color: alpha(NAVY, 0.55),
            '&.Mui-error': { color: '#E7233B' },
          },
          ...sx,
        }}
      />
    </Box>
  );
};

export default FormField;
